import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import {
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { PageBody } from '~/components/common/PageBody'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowPageLayout } from '~/components/common/WorkflowPageLayout'
import { PreviousDiffBar } from '~/components/planSubmit/PreviousDiffBar'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { convertAreaLabel } from '~/models/area'
import { formatDateDisplay, fromProtoDate } from '~/models/dateTime'
import { KOMA_TIME_LABELS } from '~/models/komaToTime'
import { DetailHeader } from '~/pages/GenerationSalesPlanDetail/components/DetailHeader'
import { PlanTable } from '~/pages/GenerationSalesPlanDetail/components/PlanTable'
import { SubmitConfirmDialog } from '~/pages/GenerationSalesPlanDetail/components/SubmitConfirmDialog'
import { useGetGenerationSalesPlanDetail } from '~/pages/GenerationSalesPlanDetail/hooks/useGetGenerationSalesPlanDetail'
import { useSubmitGenerationSalesPlan } from '~/pages/GenerationSalesPlanDetail/hooks/useSubmitGenerationSalesPlan'
import {
  type SlotValue,
  calcChangedSlotCount,
  calcGenerationDiffFlags,
  calcGenerationTotal,
  calcSlotTotal,
  calcSupplyDemandBalance,
} from '~/pages/GenerationSalesPlanDetail/models/calc'
import {
  type BreakdownColumn,
  type ColumnSide,
  buildBreakdownColumns,
  resolveColumnKey,
} from '~/pages/GenerationSalesPlanDetail/models/columns'
import { routePaths } from '~/routes/routePaths'

type BreakdownSourceRowLike = {
  sourceType: number
  counterpartyCode?: string
  counterpartyName?: string
  jepxSpotPortion?: number
  contractId?: string
  slots: Array<{ valueKwh?: bigint }>
}

/**
 * 計画値行を列キー（resolveColumnKey）でまとめたスロットマップを構築する。
 * columns.ts の buildBreakdownColumns が内部でも同様の合算を行うが、
 * ここでは前回提出データ（prevMap）専用に列キー → スロットのマップを組む。
 */
function buildPrevSlotsMap<
  T extends {
    sourceType: number
    counterpartyCode?: string
    jepxSpotPortion?: number
    contractId?: string
    slots: Array<{ valueKwh?: bigint }>
  },
>(rows: T[], side: ColumnSide): Map<string, SlotValue[]> {
  const m = new Map<string, SlotValue[]>()
  for (const row of rows) {
    const key = resolveColumnKey(row, side)
    const slots = row.slots.map((s) =>
      s.valueKwh !== undefined ? Number(s.valueKwh) : 0,
    )
    m.set(key, slots)
  }
  return m
}

export const GenerationSalesPlanDetailPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const planId = id ?? ''

  // workflow-back-bar-readonly AC-12/AC-23: 一覧から引き継いだ caseId で
  // 完了済みケースなら提出ボタンを read-only 化する
  // (fail-closed の理由は useCurrentWorkflowCase の JSDoc 参照)。
  const { isReadonly } = useCurrentWorkflowCase()

  const [isSubmitDialogOpen, setSubmitDialogOpen] = useState(false)

  // ---- データ取得 ----
  const { data, isLoading, isError } = useGetGenerationSalesPlanDetail(planId)
  const detail = data?.detail
  const { submit, isSubmitting } = useSubmitGenerationSalesPlan(planId)

  // ---- 前回差分 ----
  const previousSubmitted = detail?.previousSubmitted
  const hasPreviousSubmitted = previousSubmitted !== undefined

  // ---- 発電計画合計（発電BG別内訳行から算出。列としては表示しない） ----
  const generationTotal: SlotValue[] = useMemo(
    () =>
      detail
        ? calcGenerationTotal(detail.generationValues)
        : Array<SlotValue>(48).fill(undefined),
    [detail],
  )

  // spec AC-031: 前回差分は発電所単位の系統コード（generationBgCode）ベースで
  // 判定する。全 BG 合算後の合計同士を比較すると BG 間の相殺（+100/-100）で
  // 差分が消える false negative が起きるため、BG 単位でマッチングする
  // calcGenerationDiffFlags に委譲する。
  const generationDiffFlags: boolean[] = useMemo(
    () =>
      hasPreviousSubmitted
        ? calcGenerationDiffFlags(
            detail?.generationValues ?? [],
            previousSubmitted.generationValues,
          )
        : Array<boolean>(48).fill(false),
    [hasPreviousSubmitted, detail, previousSubmitted],
  )

  // ---- 前回提出の列マップ（差分判定用） ----
  const prevSalesMap = useMemo(
    () =>
      hasPreviousSubmitted
        ? buildPrevSlotsMap<BreakdownSourceRowLike>(
            previousSubmitted.salesValues,
            'sales',
          )
        : new Map<string, SlotValue[]>(),
    [hasPreviousSubmitted, previousSubmitted],
  )

  const prevProcurementMap = useMemo(
    () =>
      hasPreviousSubmitted
        ? buildPrevSlotsMap<BreakdownSourceRowLike>(
            previousSubmitted.procurementValues,
            'procurement',
          )
        : new Map<string, SlotValue[]>(),
    [hasPreviousSubmitted, previousSubmitted],
  )

  // ---- BreakdownColumn 配列構築（spec AC-002 列順: 販売 → 調達） ----
  const salesColumns = useMemo(
    (): BreakdownColumn[] =>
      detail
        ? buildBreakdownColumns({
            rows: detail.salesValues,
            side: 'sales',
            translate: t,
            hasPrev: hasPreviousSubmitted,
            prevMap: prevSalesMap,
          })
        : [],
    [detail, t, hasPreviousSubmitted, prevSalesMap],
  )

  const procurementColumns = useMemo(
    (): BreakdownColumn[] =>
      detail
        ? buildBreakdownColumns({
            rows: detail.procurementValues,
            side: 'procurement',
            translate: t,
            hasPrev: hasPreviousSubmitted,
            prevMap: prevProcurementMap,
          })
        : [],
    [detail, t, hasPreviousSubmitted, prevProcurementMap],
  )

  // ---- 合計・需給バランス算出（spec AC-004/005/006） ----
  const salesTotal = useMemo(
    () => calcSlotTotal(salesColumns.map((c) => c.slots)),
    [salesColumns],
  )

  const procurementTotal = useMemo(
    () => calcSlotTotal(procurementColumns.map((c) => c.slots)),
    [procurementColumns],
  )

  const balances = useMemo(
    () =>
      Array.from({ length: 48 }, (_, i) =>
        calcSupplyDemandBalance(
          generationTotal[i],
          procurementTotal[i],
          salesTotal[i],
        ),
      ),
    [generationTotal, procurementTotal, salesTotal],
  )

  // ---- 提出フロー（spec AC-032〜034） ----
  // AC-032: ダイアログには提出内容サマリー（計画タイプ / 対象期間 / エリア /
  // 各合計値 / 前回提出からの変更コマ数）を表示する（design gsOpenSubmit 準拠の順序）。
  const summaryLines = useMemo(() => {
    if (!detail) return []
    // 発電計画合計の差分だけでなく販売・調達の内訳列の変更も横断して数える
    // （union 集計。発電合計のみで判定すると販売/調達だけの変更を見逃す）。
    const changedCount = calcChangedSlotCount(
      generationDiffFlags,
      salesColumns,
      procurementColumns,
    )
    const targetPeriodLabel =
      detail.targetPeriodStartDate !== undefined
        ? formatDateDisplay(fromProtoDate(detail.targetPeriodStartDate))
        : '—'
    const sumSlots = (slots: SlotValue[]): number =>
      slots.reduce<number>((acc, v) => acc + (v ?? 0), 0)
    return [
      t('page:generationSalesPlanDetail.submitConfirm.summaryPlanType'),
      t('page:generationSalesPlanDetail.submitConfirm.summaryTargetPeriod', {
        period: targetPeriodLabel,
      }),
      t('page:generationSalesPlanDetail.submitConfirm.summaryArea', {
        area: convertAreaLabel(detail.areaCode),
      }),
      t('page:generationSalesPlanDetail.submitConfirm.summaryGenerationTotal', {
        kwh: sumSlots(generationTotal).toLocaleString(),
      }),
      t('page:generationSalesPlanDetail.submitConfirm.summarySalesTotal', {
        kwh: sumSlots(salesTotal).toLocaleString(),
      }),
      t(
        'page:generationSalesPlanDetail.submitConfirm.summaryProcurementTotal',
        { kwh: sumSlots(procurementTotal).toLocaleString() },
      ),
      t('page:generationSalesPlanDetail.submitConfirm.summaryChangedCount', {
        count: changedCount,
      }),
    ]
  }, [
    detail,
    generationDiffFlags,
    generationTotal,
    salesColumns,
    salesTotal,
    procurementColumns,
    procurementTotal,
    t,
  ])

  const handleSubmitClick = (): void => {
    setSubmitDialogOpen(true)
  }

  const handleConfirmSubmit = (): void => {
    submit()
    setSubmitDialogOpen(false)
  }

  const breadcrumbItems = [
    {
      label: t('common:nav.plan'),
      to: routePaths.planGenSales.path,
    },
    {
      label: t('common:route.planGenSales'),
      to: routePaths.planGenSales.path,
    },
    {
      label: t('common:route.planGenSalesDetail'),
      bold: true,
    },
  ]

  return (
    <WorkflowPageLayout breadcrumbItems={breadcrumbItems}>
      <PageBody>
        {isError ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: LightModeSemanticColors.Text.midEmphasis.value,
              fontSize: fontSizes.md.value,
            }}
          >
            {t('page:generationSalesPlanDetail.error.loadFailed')}
          </div>
        ) : (
          <RegionLoading
            flexDirection="column"
            flex="1"
            gap={4}
            display="flex"
            minHeight={isLoading || !detail ? '200px' : 0}
            isLoading={isLoading || !detail}
          >
            {isLoading || !detail ? null : (
              <>
                <DetailHeader
                  fileCreatedAt={detail.fileCreatedAt}
                  isReadonly={isReadonly}
                  isSubmitting={isSubmitting}
                  onSubmit={handleSubmitClick}
                  previousSubmittedAt={previousSubmitted?.submittedAt}
                  receiptCheckSuspendedByCallLimit={
                    detail.receiptCheckSuspendedByCallLimit
                  }
                  status={detail.status}
                  submissionDeadlineAt={detail.submissionDeadlineAt}
                  submissionEntityCode={detail.submissionEntityCode}
                  submissionEntityName={detail.submissionEntityName}
                  submittedAt={detail.submittedAt}
                />

                {hasPreviousSubmitted && (
                  <PreviousDiffBar changedLabelKey="page:generationSalesPlanDetail.previousDiff.changedLabel" />
                )}

                <PageScrollArea>
                  <PlanTable
                    balances={balances}
                    generationDiffFlags={generationDiffFlags}
                    generationTotal={generationTotal}
                    hasPreviousSubmitted={hasPreviousSubmitted}
                    procurementColumns={procurementColumns}
                    procurementTotal={procurementTotal}
                    salesColumns={salesColumns}
                    salesTotal={salesTotal}
                    timeLabels={KOMA_TIME_LABELS}
                  />
                </PageScrollArea>

                <SubmitConfirmDialog
                  isOpen={isSubmitDialogOpen}
                  isSubmitting={isSubmitting}
                  onClose={() => {
                    setSubmitDialogOpen(false)
                  }}
                  onConfirm={handleConfirmSubmit}
                  summaryLines={summaryLines}
                />
              </>
            )}
          </RegionLoading>
        )}
      </PageBody>
    </WorkflowPageLayout>
  )
}
